import { useState } from 'react';
import { StickyNote, Save, X, Edit2 } from 'lucide-react';

// NOTES about a business — what a human wants to remember about the calls.
//
// `campaign_contacts.outcomeNotes` has always been RENDERED on the expanded row
// and has always been accepted by `contactPatchSchema` — there was simply never an
// input, so the only way to write it was the classifier or SQL. This is that input.
//
// Deliberately NOT the same thing as the follow-up "matter":
//   • NOTES are for the humans. Free text, never sent to the AI, no length pressure.
//   • The MATTER on a follow-up is an instruction TO the AI — it becomes that call's
//     objective.
// Conflating them would mean a private aside about a customer ends up being read out
// on the next call, so the copy on each says which is which.

export default function ContactNotes({ contact, canEdit = false, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const value = contact?.outcomeNotes || '';

  const start = () => { setDraft(value); setError(''); setEditing(true); };

  const save = async () => {
    const next = draft.trim();
    if (next === value.trim()) { setEditing(false); return; }
    setBusy(true);
    setError('');
    try {
      // null clears the column; the server treats an absent key as leave-alone.
      await onSave({ outcomeNotes: next || null });
      setEditing(false);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not save the note.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-stone-800 bg-stone-900/30 p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StickyNote size={14} className="text-stone-400" />
          <h4 className="text-sm font-medium text-stone-200">Notes</h4>
        </div>
        {canEdit && !editing && (
          <button type="button" className="btn-ghost" onClick={start}>
            <Edit2 size={13} /> {value ? 'Edit' : 'Add a note'}
          </button>
        )}
      </div>
      <p className="mb-2 text-xs text-stone-500">
        For your team. Not read by the AI and never spoken on a call — to tell the AI something, use
        the objective on a follow-up.
      </p>

      {error && (
        <p className="mb-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</p>
      )}

      {editing ? (
        <div className="space-y-2">
          <textarea
            className="field-input min-h-[90px] text-sm"
            placeholder="e.g. gatekeeper is Denise, mornings are better, they re-tender lanes in October"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={1000}
            autoFocus
          />
          <div className="flex items-center gap-2">
            <button type="button" className="btn-primary" onClick={save} disabled={busy}>
              <Save size={13} /> {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)} disabled={busy}>
              <X size={13} /> Cancel
            </button>
            <span className="ml-auto text-xs tabular-nums text-stone-500">{draft.length} / 1000</span>
          </div>
        </div>
      ) : value ? (
        <p className="whitespace-pre-wrap text-sm text-stone-300">{value}</p>
      ) : (
        <p className="text-sm text-stone-500">No notes yet.</p>
      )}
    </div>
  );
}
